import ElectricalSvg from '@/assets/flight/icons/assistant/electrical.svg?react'
import MobilitySvg from '@/assets/flight/icons/assistant/mobility.svg?react'
import PetSvg from '@/assets/flight/icons/assistant/pet.svg?react'
import SpecialSvg from '@/assets/flight/icons/assistant/special.svg?react'
import Checkbox from '@/components/Checkbox/Checkbox'
import RadioGroup from '@/components/RadioGroup/RadioGroup'
import Select from '@/components/Select/Select'
import Switch from '@/components/Switch/Switch'
import {
  MEDICAL_EQUIPMENT_ITEMS,
  MOBILITY_ASSISTANCE_ITEMS,
  MOBILITY_DEVICES,
  SPECIAL_ASSISTANCE_ITEMS,
} from '@/lib/flight/constants'
import {
  useMedicalEquipment,
  useMedicalEquipmentType,
  useMobilityAssistance,
  useMobilityAssistanceType,
  useMobilityDevice,
  useNeedAssistance,
  useServiceAnimal,
  useSpecialAssistance,
  useSpecialAssistanceType,
} from '@/lib/flight/store'
import {type AssistanceInformation} from '@/lib/flight/types'
import {MobilityAssistanceType} from '@/lib/flight/types'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import style from '@/views/Flight/FlightDetails.module.scss'

export interface AssistanceFormControl {
  getValues: () => AssistanceInformation
}

interface Props {
  ref?: Ref<AssistanceFormControl>
}

export default memo<Props>(function AssistanceForm({ref}) {
  const [needAssistance, setNeedAssistance] = useNeedAssistance()
  const [mobilityAssistance, setMobilityAssistance] = useMobilityAssistance()
  const [mobilityAssistanceType, setMobilityAssistanceType] = useMobilityAssistanceType()
  const [mobilityDevice, setMobilityDevice] = useMobilityDevice()
  const [specialAssistance, setSpecialAssistance] = useSpecialAssistance()
  const [specialAssistanceType, setSpecialAssistanceType] = useSpecialAssistanceType()
  const [serviceAnimal, setServiceAnimal] = useServiceAnimal()
  const [medicalEquipment, setMedicalEquipment] = useMedicalEquipment()
  const [medicalEquipmentType, setMedicalEquipmentType] = useMedicalEquipmentType()

  const latestNeedAssistance = useLatest(needAssistance)
  const latestMobilityAssistance = useLatest(mobilityAssistance)
  const latestMobilityAssistanceType = useLatest(mobilityAssistanceType)
  const latestMobilityDevice = useLatest(mobilityDevice)
  const latestSpecialAssistance = useLatest(specialAssistance)
  const latestSpecialAssistanceType = useLatest(specialAssistanceType)
  const latestServiceAnimal = useLatest(serviceAnimal)
  const latestMedicalEquipment = useLatest(medicalEquipment)
  const latestMedicalEquipmentType = useLatest(medicalEquipmentType)

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => ({
        needAssistance: latestNeedAssistance.current,
        mobilityAssistance: latestMobilityAssistance.current,
        mobilityAssistanceType: latestMobilityAssistanceType.current,
        mobilityDevice: latestMobilityDevice.current,
        specialAssistance: latestSpecialAssistance.current,
        specialAssistanceType: latestSpecialAssistanceType.current,
        serviceAnimal: latestServiceAnimal.current,
        medicalEquipment: latestMedicalEquipment.current,
        medicalEquipmentType: latestMedicalEquipmentType.current,
      }),
    }),
    [],
  )

  return (
    <>
      <div className={style.AssistanceSwitch}>
        <Switch
          isSelected={needAssistance}
          onChange={setNeedAssistance}
          isLabelVisible
          label='I want to have the assistance at the airport'
          aria-label='Request assistance at airport'
        />
      </div>
      {needAssistance && (
        <div className={style.AssistanceGroups}>
          <div className={style.AssistanceGroup}>
            <Checkbox
              label={
                <div className={style.AssistanceGroupLabel}>
                  <MobilitySvg />
                  Mobility
                </div>
              }
              className={style.AssistanceGroupLegend}
              isLabelVisible
              isSelected={mobilityAssistance}
              onChange={setMobilityAssistance}
              aria-expanded={mobilityAssistance}
              aria-label='Toggle mobility assistance options'
            />
            <div className={style.AssistanceGroupContent}>
              <VisuallyHidden>
                <h3>Mobility</h3>
              </VisuallyHidden>
              <RadioGroup
                value={mobilityAssistanceType}
                onChange={setMobilityAssistanceType}
                isDisabled={!mobilityAssistance}
                items={MOBILITY_ASSISTANCE_ITEMS}
              />
              {mobilityAssistanceType === MobilityAssistanceType.PersonalWheelchair && (
                <Select
                  isDisabled={!mobilityAssistance}
                  className={style.Select}
                  label='Type of mobility device'
                  options={MOBILITY_DEVICES}
                  selectedItem={mobilityDevice}
                  setSelectedItem={setMobilityDevice}
                />
              )}
            </div>
          </div>
          <div className={style.AssistanceGroup}>
            <Checkbox
              label={
                <div className={style.AssistanceGroupLabel}>
                  <SpecialSvg />
                  Special assistance needed at the airport
                </div>
              }
              className={style.AssistanceGroupLegend}
              isLabelVisible
              isSelected={specialAssistance}
              onChange={setSpecialAssistance}
              aria-expanded={specialAssistance}
              aria-label='Toggle special assistance options'
            />
            <div className={style.AssistanceGroupContent}>
              <VisuallyHidden>
                <h3>Special assistance needed at the airport</h3>
              </VisuallyHidden>
              <RadioGroup
                isDisabled={!specialAssistance}
                value={specialAssistanceType}
                onChange={setSpecialAssistanceType}
                items={SPECIAL_ASSISTANCE_ITEMS}
              />
            </div>
          </div>
          <div className={style.AssistanceGroup}>
            <Checkbox
              label={
                <div className={style.AssistanceGroupLabel}>
                  <PetSvg className={style.Fill} />
                  Traveling with a service animal
                </div>
              }
              className={style.AssistanceGroupLegend}
              isLabelVisible
              isSelected={serviceAnimal}
              onChange={setServiceAnimal}
            />
          </div>
          <div className={style.AssistanceGroup}>
            <VisuallyHidden>
              <h3>Traveling with a service animal</h3>
            </VisuallyHidden>
            <Checkbox
              label={
                <div className={style.AssistanceGroupLabel}>
                  <ElectricalSvg />
                  Electrical medical equipment
                </div>
              }
              className={style.AssistanceGroupLegend}
              isLabelVisible
              isSelected={medicalEquipment}
              onChange={setMedicalEquipment}
              aria-expanded={medicalEquipment}
              aria-label='Toggle medical equipment options'
            />
            <div className={style.AssistanceGroupContent}>
              <VisuallyHidden>
                <h3>Electrical medical equipment</h3>
              </VisuallyHidden>
              <RadioGroup
                isDisabled={!medicalEquipment}
                value={medicalEquipmentType}
                onChange={setMedicalEquipmentType}
                items={MEDICAL_EQUIPMENT_ITEMS}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
})
